import { useEffect, useState, useCallback } from 'react'
import { useCurrenciesStore } from '@/store/currenciesStore'
import { useAuthStore } from '@/store/authStore'
import { toast } from "sonner";

// Static flag to track initialization across all instances of the hook
let globalInitializationAttempted = false;

// Helper function to wait for auth to be ready
const waitForAuthReady = async () => {
  // Simple implementation that checks if the user is authenticated
  const authStore = useAuthStore.getState();
  return authStore.isAuthenticated && !!authStore.user;
};

// Helper function to force token refresh
const forceTokenRefresh = async () => {
  // This is a no-op in the web version
  return;
};

export function useCurrencyConversion() {
  const { 
    currencies, 
    exchangeRates, 
    lastRatesUpdate, 
    fetchExchangeRates,
    convertAmount: convertAmountFromStore,
    forceUpdateExchangeRates
  } = useCurrenciesStore()
  const { user } = useAuthStore()
  const [isInitialized, setIsInitialized] = useState(false)
  const [isOfflineMode, setIsOfflineMode] = useState(false)
  const [initializationAttempted, setInitializationAttempted] = useState(globalInitializationAttempted)

  // Initialize exchange rates
  useEffect(() => {
    const initializeExchangeRates = async () => {
      // Skip if we've already attempted initialization globally
      if (globalInitializationAttempted) {
        setIsInitialized(true)
        return
      }
      
      // Set both local and global initialization flags
      setInitializationAttempted(true)
      globalInitializationAttempted = true
      
      try {
        // Wait for auth to be ready before proceeding
        const isAuthenticated = await waitForAuthReady()
        if (!isAuthenticated || !user?.id) {
          console.log('❌ User not authenticated, skipping exchange rates initialization')
          setIsOfflineMode(true)
          setIsInitialized(true) // Mark as initialized even if we're offline
          return
        }

        // Skip if exchange rates are already loaded
        if (exchangeRates && Object.keys(exchangeRates).length > 0) {
          console.log(`✅ Exchange rates already loaded (${Object.keys(exchangeRates).length} currencies), skipping redundant fetch`)
          setIsInitialized(true)
          setIsOfflineMode(false)
          return
        }

        console.log('🔍 Initializing exchange rates...')
        
        // Try to force refresh the token before fetching exchange rates
        await forceTokenRefresh()
        
        await fetchExchangeRates(false) // Use cache if available during initialization
        
        // Use a small timeout to ensure state has updated before logging
        setTimeout(() => {
          if (exchangeRates && Object.keys(exchangeRates).length > 0) {
            console.log(`✅ Loaded exchange rates for ${Object.keys(exchangeRates).length} currencies`)
            setIsOfflineMode(false)
          } else {
            console.log('⚠️ Exchange rates not loaded, may be in offline mode')
            setIsOfflineMode(true)
          }
          setIsInitialized(true)
        }, 300)
      } catch (error) {
        console.error('❌ Failed to initialize exchange rates:', error)
        toast.error("Failed to initialize currency conversion")
        setIsOfflineMode(true)
        setIsInitialized(true) // Mark as initialized even if we failed
      }
    }

    // Only initialize if we have a user and haven't attempted initialization yet globally
    if (user?.id && !globalInitializationAttempted) {
      initializeExchangeRates()
    } else if (!user?.id || globalInitializationAttempted) {
      // If no user or already initialized globally, mark as initialized to prevent loading attempts
      setIsInitialized(true)
      setInitializationAttempted(true)
    }
  }, [fetchExchangeRates, exchangeRates, user?.id, initializationAttempted])

  // Get default currency
  const defaultCurrency = currencies.find(c => c.isDefault)?.code || 'USD'

  // Convert to default currency
  const convertToDefault = useCallback((amount: number, fromCurrency: string) => {
    if (isOfflineMode || !exchangeRates) {
      // In offline mode, just return the original amount
      return amount
    }
    
    // Use the convertAmount function from the store
    return convertAmountFromStore(amount, fromCurrency, defaultCurrency)
  }, [isOfflineMode, exchangeRates, convertAmountFromStore, defaultCurrency])

  const formatAmount = useCallback((amount: number, currencyCode: string) => {
    const currency = currencies.find(c => c.code === currencyCode)
    const symbol = currency?.symbol || '$'
    
    // Handle invalid input
    if (typeof amount !== 'number' || isNaN(amount)) {
      console.warn(`[CurrencyConversion] Invalid amount to format: ${amount}`)
      amount = 0
    }
    
    return new Intl.NumberFormat(undefined, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace(/^/, symbol)
  }, [currencies])

  const updateRates = useCallback(async () => {
    if (!user?.id) {
      console.log('❌ User not authenticated, cannot update exchange rates')
      return false
    }
    
    console.log('🔄 Updating exchange rates directly from API...')

    try {
      // Force token refresh before updating rates
      await forceTokenRefresh()
      
      // Use the new direct API fetch method
      const rates = await forceUpdateExchangeRates()
      
      // Check if rates were actually loaded
      if (rates && Object.keys(rates).length > 0) {
        console.log('✅ Exchange rates updated successfully from API')
        setIsOfflineMode(false)
        return true
      } else {
        console.log('⚠️ Exchange rates update failed, may be in offline mode')
        setIsOfflineMode(true)
        return false
      }
    } catch (error) {
      console.error('❌ Failed to update exchange rates:', error)
      toast.error("Failed to update exchange rates")
      setIsOfflineMode(true)
      return false
    }
  }, [user?.id, forceUpdateExchangeRates])

  const formatLastUpdate = useCallback(() => {
    if (!lastRatesUpdate) return null
    const date = new Date(lastRatesUpdate)
    if (isNaN(date.getTime())) return null
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }, [lastRatesUpdate])

  return {
    defaultCurrency,
    convertToDefault,
    convertAmount: convertAmountFromStore,
    isInitialized,
    isOfflineMode,
    formatAmount,
    updateRates,
    exchangeRates,
    lastUpdate: formatLastUpdate()
  }
} 